import { useState, useRef, useEffect } from 'react'
import { NavLink, Link, useLocation } from 'react-router-dom'
import { Moon, Sun, ChevronDown, Menu, X, Headphones } from 'lucide-react'
import { useTheme } from '../../context/ThemeContext.jsx'
import { useLanguage } from '../../context/LanguageContext.jsx'

const LANGUAGES = [
  { code: 'en', name: 'English',    flag: '🇬🇧' },
  { code: 'es', name: 'Español',    flag: '🇪🇸' },
  { code: 'fr', name: 'Français',   flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch',    flag: '🇩🇪' },
  { code: 'pt', name: 'Português',  flag: '🇧🇷' },
  { code: 'it', name: 'Italiano',   flag: '🇮🇹' },
  { code: 'ru', name: 'Русский',    flag: '🇷🇺' },
  { code: 'ar', name: 'العربية',    flag: '🇸🇦' },
  { code: 'zh', name: '中文',       flag: '🇨🇳' },
  { code: 'ja', name: '日本語',     flag: '🇯🇵' },
]

function cn(...args) {
  return args.filter(Boolean).join(' ')
}

export default function Header() {
  const { toggleTheme, isDark, isReady } = useTheme()
  const { lang, setLang, t } = useLanguage()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const location = useLocation()
  const langRef = useRef(null)

  const currentLang = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0]

  // Close lang dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  const navLinkClass = (to, end) => {
    const active = end ? location.pathname === to : location.pathname === to
    return cn(
      'text-sm font-medium transition-colors hover:text-primary px-3 py-2 rounded-lg hover:bg-primary/5',
      active ? 'text-primary bg-primary/5' : 'text-muted-foreground'
    )
  }

  const mobileLinkClass = (to) => {
    const active = location.pathname === to
    return cn(
      'flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
      active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-primary/5 hover:text-primary'
    )
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-xl" role="banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">

        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2 sm:gap-3 group flex-shrink-0"
          aria-label="FastAudio - Convert Videos to MP3"
        >
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white shadow-lg shadow-primary/25 group-hover:scale-110 transition-transform">
            <Headphones className="w-4 sm:w-5 h-4 sm:h-5" aria-hidden="true" />
          </div>
          <span className="font-display font-bold text-lg sm:text-xl tracking-tight">
            Fast<span className="text-primary">Audio</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden sm:flex items-center gap-1 sm:gap-4" role="navigation" aria-label="Primary navigation">
          <Link to="/" className={navLinkClass('/', true)} aria-current={location.pathname === '/' ? 'page' : undefined}>
            {t('home')}
          </Link>
          <Link to="/faqs" className={navLinkClass('/faqs')}>
            {t('faqs')}
          </Link>
          <Link to="/youtube-downloader" className={navLinkClass('/youtube-downloader')}>
            {t('youtubeDownloader')}
          </Link>
          <Link to="/instagram-downloader" className={navLinkClass('/instagram-downloader')}>
            {t('instagramDownloader')}
          </Link>
          <Link to="/tiktok-downloader" className={navLinkClass('/tiktok-downloader')}>
            {t('tiktokDownloader')}
          </Link>
          <Link to="/facebook-downloader" className={navLinkClass('/facebook-downloader')}>
            {t('facebookDownloader')}
          </Link>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-primary/10 transition-colors text-muted-foreground hover:text-primary"
            aria-label="Toggle dark mode"
          >
            {isReady ? (isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />) : <div className="w-5 h-5" />}
          </button>

          {/* Language dropdown */}
          <div className="relative language-dropdown" ref={langRef}>
            <button
              onClick={() => setLangOpen(v => !v)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-primary/10 transition-colors text-muted-foreground hover:text-primary text-sm font-medium language-dropdown"
              aria-label="Change language"
            >
              <span>{currentLang.flag}</span>
              <span className="hidden md:inline">{currentLang.name}</span>
              <ChevronDown className={cn('w-4 h-4 transition-transform', langOpen && 'rotate-180')} />
            </button>

            {langOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-xl shadow-lg z-50 max-h-64 overflow-y-auto language-dropdown">
                {LANGUAGES.map(l => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangOpen(false) }}
                    className={cn(
                      'w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 transition-colors hover:bg-primary/10 first:rounded-t-xl last:rounded-b-xl',
                      lang === l.code ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:text-primary'
                    )}
                  >
                    <span>{l.flag}</span>
                    <span>{l.name}</span>
                    {lang === l.code && <span className="ml-auto text-xs">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* Mobile controls */}
        <div className="flex sm:hidden items-center gap-2">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-primary/10 transition-colors text-muted-foreground hover:text-primary"
            aria-label="Toggle dark mode"
          >
            {isReady ? (isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />) : <div className="w-5 h-5" />}
          </button>
          <button
            onClick={() => setMobileOpen(v => !v)}
            className="p-2 rounded-lg hover:bg-primary/10 transition-colors text-muted-foreground hover:text-primary"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="sm:hidden border-t border-border/40 bg-background/95 backdrop-blur-xl px-4 py-3 space-y-1">
          <Link to="/" className={mobileLinkClass('/')}>{t('home')}</Link>
          <Link to="/faqs" className={mobileLinkClass('/faqs')}>{t('faqs')}</Link>
          <Link to="/youtube-downloader" className={mobileLinkClass('/youtube-downloader')}>{t('youtubeDownloader')}</Link>
          <Link to="/instagram-downloader" className={mobileLinkClass('/instagram-downloader')}>{t('instagramDownloader')}</Link>
          <Link to="/tiktok-downloader" className={mobileLinkClass('/tiktok-downloader')}>{t('tiktokDownloader')}</Link>
          <Link to="/facebook-downloader" className={mobileLinkClass('/facebook-downloader')}>{t('facebookDownloader')}</Link>

          {/* Language section */}
          <div className="pt-2 border-t border-border/40">
            <p className="px-3 py-1 text-xs text-muted-foreground font-medium uppercase tracking-wider">{t('language')}</p>
            <div className="grid grid-cols-2 gap-1 mt-1">
              {LANGUAGES.map(l => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                    lang === l.code ? 'bg-primary/10 text-primary font-medium' : 'text-muted-foreground hover:bg-primary/5'
                  )}
                >
                  <span>{l.flag}</span>
                  <span className="truncate">{l.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
